import React from "react";
import { PenTool } from "lucide-react";
import AIStudioTool from "./AIStudioTool";

const FIELDS = [
  { key: "brand_name", label: "Brand name", required: true, wide: true,
    placeholder: "e.g. Sunrise Oat, Herbally, Northwild Snacks…",
    hint: "This exact text will be rendered inside the logo — spell it as it should appear." },

  { key: "tagline", label: "Tagline",
    placeholder: "e.g. Real ingredients, real mornings.",
    hint: "Optional. Helps direct the mark's mood.", maxLength: 140 },

  { key: "category", label: "Category", type: "category",
    hint: "Pick the CPG shelf you sit on — or type your own if it's niche." },

  { key: "aspirational_brands", label: "Aspirational brands", wide: true,
    placeholder: "e.g. Olipop, Ghia, Fly by Jing, Liquid Death",
    hint: "Comma-separated brands whose aesthetic to evoke (not copy).",
    maxLength: 200 },

  // Rich typography picker with hover previews. Renders the founder's actual
  // brand name in each font style so they can eyeball each option.
  { key: "typography_direction", label: "Typography direction", type: "typography",
    previewFrom: "brand_name", wide: true,
    hint: "Scroll the menu to see how your brand name looks in each direction." },

  { key: "style", label: "Style direction",
    placeholder: "e.g. modern wordmark, retro monogram, hand-drawn emblem…",
    hint: "Design mood — how should it feel?" },

  { key: "color_notes", label: "Colour notes",
    placeholder: "e.g. terracotta + cream, or navy + gold",
    hint: "Optional. Leave blank to let AI decide." },

  { key: "count", label: "Concepts to generate", type: "number", min: 1, max: 4,
    hint: "How many logo variations to render (1-4)." },

  { key: "quality", label: "Render quality", type: "select",
    options: [
      { value: "low",    label: "Low — fastest previews" },
      { value: "medium", label: "Medium — balanced" },
      { value: "high",   label: "High — portfolio-ready (recommended)" },
    ] },
];

const DEFAULTS = {
  brand_name: "",
  tagline: "",
  category: "",
  aspirational_brands: "",
  typography_direction: "modern_grotesk",
  style: "modern wordmark",
  color_notes: "",
  count: 4,
  quality: "high",
};

// Admin QA presets.
const DEMO_INPUTS = [
  {
    label: "Premium editorial beverage",
    hint: "Serif wordmark · Ghia-adjacent · warm palette",
    values: {
      brand_name: "Sunrise Oat", tagline: "Real ingredients, real mornings.",
      category: "beverage", aspirational_brands: "Ghia, Oatly, Aesop, Byredo",
      typography_direction: "editorial_serif", style: "premium editorial wordmark, hairline serif, elegant restraint",
      color_notes: "warm terracotta + cream", count: 4, quality: "high",
    },
  },
  {
    label: "Retro snack",
    hint: "Bubble letters · high-sat energy",
    values: {
      brand_name: "Chip Riot", tagline: "Snack Loud.",
      category: "snacks", aspirational_brands: "Doritos, Takis, Flamin' Hot",
      typography_direction: "retro_70s", style: "retro bubble wordmark with chrome edge, sticker-ready",
      color_notes: "electric blue + hot pink on black", count: 4, quality: "high",
    },
  },
  {
    label: "Wellness supplement",
    hint: "Clean apothecary · medical calm",
    values: {
      brand_name: "Field & Marrow", tagline: "Daily science, quiet ritual.",
      category: "supplements", aspirational_brands: "Athletic Greens, Moon Juice, Sakara",
      typography_direction: "modern_grotesk", style: "apothecary-clean wordmark with a subtle botanical mark",
      color_notes: "sage + bone", count: 4, quality: "high",
    },
  },
  {
    label: "Playful pet CPG",
    hint: "Hand-drawn · warm illustrative",
    values: {
      brand_name: "Rufflebite", tagline: "Treats for good boys and girls.",
      category: "pet", aspirational_brands: "Fable, Beau Life, Bark",
      typography_direction: "hand_drawn", style: "hand-drawn wordmark with a tiny paw or bone integrated into a letter",
      color_notes: "harvest orange + cream", count: 4, quality: "high",
    },
  },
];

export default function AIStudioLogo() {
  return (
    <AIStudioTool
      toolSlug="logo"
      name="Logo Designer"
      Icon={PenTool}
      tagline="Explore logo concepts iterated on your positioning notes. Warren's AI writes a brief for the mark's mood, palette, and typography — then renders variations with your brand name baked into every mark. Drop reference logos to anchor the aesthetic, and use 'Regenerate with feedback' to iterate on any result."
      fields={FIELDS}
      endpoint="/ai-studio/logo/generate"
      defaultValues={DEFAULTS}
      demoInputs={DEMO_INPUTS}
      cta="Generate logo concepts"
      historyLabel="Your logo concepts"
      supportsReferences
      supportsFeedback
      footerNote="Concepts render in about 45–90 seconds. Warren always renders the brand name inside the logo — spell it exactly."
    />
  );
}
